import React, { useState, useEffect, useCallback, useRef } from 'react';
import Sidebar from './components/Sidebar';
import EmailList from './components/EmailList';
import EmailView from './components/EmailView';
import CalendarView from './components/CalendarView';
import Compose from './components/Compose';
import Settings from './components/Settings';
import Login from './components/Login';
import UserManagement from './components/UserManagement';
import { getToken, getUser, setSession, clearSession, getSignature, saveSignature } from './auth';
import { getFolders, getMessages, getMessage, getCategories, getPins, togglePin } from './api';
import { buildCategoryMap, KNOWN_CATEGORIES, colorByName } from './categories';
import './App.css';

export default function App() {
  const [authUser, setAuthUser] = useState(() => getToken() ? getUser() : null);
  const [showUsers, setShowUsers] = useState(false);

  function handleLogin(token, user) { setSession(token, user); setAuthUser(user); }
  function handleLogout() { clearSession(); setAuthUser(null); }

  if (!authUser) return <Login onLogin={handleLogin} />;

  return <MailApp authUser={authUser} onLogout={handleLogout} showUsers={showUsers} setShowUsers={setShowUsers} />;
}

function MailApp({ authUser, onLogout, showUsers, setShowUsers }) {
  const [sidebarWidth, setSidebarWidth] = useState(240);
  const dragRef = useRef(null);

  function onResizeMouseDown(e) {
    e.preventDefault();
    const startX = e.clientX;
    const startW = sidebarWidth;
    function onMove(ev) {
      setSidebarWidth(Math.min(480, Math.max(160, startW + ev.clientX - startX)));
    }
    function onUp() {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      if (dragRef.current) dragRef.current.classList.remove('dragging');
    }
    if (dragRef.current) dragRef.current.classList.add('dragging');
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [composing, setComposing] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [categoryMap, setCategoryMap] = useState({});
  const [folderRefreshTick, setFolderRefreshTick] = useState(0);
  const [pinnedIds, setPinnedIds] = useState([]);
  const [signature, setSignature] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [error, setError] = useState('');

  const PAGE_SIZE = 50;

  useEffect(() => {
    getFolders()
      .then(data => {
        setFolders(data);
        setLoadingFolders(false);
        const inbox = data.find(f => f.displayName.toLowerCase() === 'inbox') || data[0];
        if (inbox) setSelectedFolder(inbox);
      })
      .catch(e => { setLoadingFolders(false); setError('Failed to load folders: ' + e.message); });

    getCategories()
      .then(cats => {
        const list = cats && cats.length ? cats : KNOWN_CATEGORIES;
        setAllCategories(list);
        setCategoryMap(buildCategoryMap(list));
      })
      .catch(() => {
        setAllCategories(KNOWN_CATEGORIES);
        const map = {};
        for (const c of KNOWN_CATEGORIES) map[c.displayName] = colorByName(c.displayName);
        setCategoryMap(map);
      });

    getPins().then(setPinnedIds).catch(() => {});
    getSignature().then(setSignature).catch(() => {});

  }, []);

  const loadMessages = useCallback(async (folder, searchTerm = '', skipCount = 0, activeFilter = 'all') => {
    if (!folder) return;
    setLoadingMessages(true);
    setError('');
    try {
      const res = await getMessages(folder.id, { skip: skipCount, top: PAGE_SIZE, search: searchTerm, filter: activeFilter });
      if (skipCount === 0) {
        setMessages(res.messages);
        setSelectedMessage(null);
      } else {
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          return [...prev, ...res.messages.filter(m => !existingIds.has(m.id))];
        });
      }
      setHasMore(res.messages.length === PAGE_SIZE);
      setSkip(skipCount + res.messages.length);
    } catch (e) {
      setError('Failed to load messages: ' + e.message);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    if (selectedFolder) {
      setSearch('');
      setFilter('all');
      setSkip(0);
      loadMessages(selectedFolder, '', 0, 'all');
    }
  }, [selectedFolder, loadMessages]);

  // SSE push — subscribe to live updates for the selected folder
  useEffect(() => {
    if (!selectedFolder) return;
    const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api/mail').replace(/\/api\/mail$/, '');
    const token = getToken();
    const es = new EventSource(`${apiBase}/api/mail/folders/${selectedFolder.id}/stream?token=${token}`);
    let opened = false;
    es.onopen = () => {
      if (opened) {
        // Reconnection — full refresh to catch anything missed during the gap
        loadMessages(selectedFolder, search, 0, filter);
      }
      opened = true;
    };
    let folderRefreshTimer = null;
    function scheduleFolderRefresh() {
      if (folderRefreshTimer) return; // already scheduled
      folderRefreshTimer = setTimeout(() => {
        folderRefreshTimer = null;
        getFolders().then(data => { setFolders(data); setFolderRefreshTick(t => t + 1); }).catch(() => {});
      }, 30000); // at most once per 30s
    }

    es.onmessage = (e) => {
      const payload = JSON.parse(e.data);
      if (payload.type === 'changed') {
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const incoming = [];
          const updates = new Map();
          for (const msg of payload.messages) {
            if (existingIds.has(msg.id)) updates.set(msg.id, msg);
            else incoming.push(msg);
          }
          const updated = updates.size
            ? prev.map(m => updates.has(m.id) ? { ...m, ...updates.get(m.id) } : m)
            : prev;
          return incoming.length ? [...incoming, ...updated] : updated;
        });
        setSelectedMessage(prev => {
          if (!prev) return prev;
          const update = payload.messages.find(m => m.id === prev.id);
          return update ? { ...prev, ...update } : prev;
        });
        scheduleFolderRefresh();
      } else if (payload.type === 'deleted') {
        const removedIds = new Set(payload.ids);
        setMessages(prev => prev.filter(m => !removedIds.has(m.id)));
        setSelectedMessage(prev => (prev && removedIds.has(prev.id)) ? null : prev);
        scheduleFolderRefresh();
      } else if (payload.type === 'reload') {
        loadMessages(selectedFolder, search, 0, filter);
        scheduleFolderRefresh();
      }
    };
    return () => es.close();
  }, [selectedFolder, loadMessages, search, filter]);

  async function handleSelectMessage(msg) {
    setLoadingMessage(true);
    setComposing(false);
    try {
      const full = await getMessage(msg.id);
      setSelectedMessage(full);
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, isRead: true } : m));
    } catch (e) {
      setError('Failed to load message: ' + e.message);
    } finally {
      setLoadingMessage(false);
    }
  }

  function handleSearch(term) {
    setSearch(term);
    setSkip(0);
    loadMessages(selectedFolder, term, 0, filter);
  }

  function handleFilterChange(f) {
    setFilter(f);
    setSkip(0);
    loadMessages(selectedFolder, search, 0, f);
  }

  function handleLoadMore() {
    loadMessages(selectedFolder, search, skip, filter);
  }

  function handleMessageUpdate(id, changes) {
    if (changes === null) {
      setMessages(prev => prev.filter(m => m.id !== id));
      if (selectedMessage?.id === id) setSelectedMessage(null);
      return;
    }
    setMessages(prev => prev.map(m => m.id === id ? { ...m, ...changes } : m));
    if (selectedMessage?.id === id) setSelectedMessage(prev => ({ ...prev, ...changes }));
  }

  function handleDeleted(id) { handleMessageUpdate(id, null); }
  function handleMoved(id) { handleMessageUpdate(id, null); }

  const undoTimerRef = useRef(null);
  const [undoState, setUndoState] = useState(null); // { label, fn }

  function registerUndo(label, fn) {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    const tid = setTimeout(() => setUndoState(null), 5000);
    undoTimerRef.current = tid;
    setUndoState({ label, fn });
  }

  async function handleUndo() {
    if (!undoState) return;
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    const fn = undoState.fn;
    setUndoState(null);
    try {
      await fn();
      loadMessages(selectedFolder, search, 0, filter);
    } catch (e) {
      setError('Undo failed: ' + e.message);
    }
  }

  async function handleTogglePin(id) {
    const next = await togglePin(id);
    setPinnedIds(next);
  }

  async function handleSaveSignature(sig) {
    setSignature(sig);
    await saveSignature(sig).catch(() => {});
    setShowSettings(false);
  }

  return (
    <div className="app" style={{ gridTemplateColumns: `${sidebarWidth}px 340px 1fr` }}>
      <div className="sidebar-col">
      <Sidebar
        folders={folders}
        selectedFolder={selectedFolder}
        authUser={authUser}
        onLogout={onLogout}
        refreshTick={folderRefreshTick}
        loadingFolders={loadingFolders}
        onSelect={f => { setSelectedFolder(f); setComposing(false); setShowSettings(false); setShowUsers(false); setShowCalendar(false); }}
        onCompose={() => { setComposing(true); setSelectedMessage(null); setShowSettings(false); setShowUsers(false); setShowCalendar(false); }}
        onSettings={() => { setShowSettings(true); setComposing(false); setShowUsers(false); setShowCalendar(false); }}
        onUsers={authUser.role === 'admin' ? () => { setShowUsers(true); setShowSettings(false); setComposing(false); setShowCalendar(false); } : null}
        onCalendar={() => { setShowCalendar(true); setShowSettings(false); setShowUsers(false); setComposing(false); }}
        showingCalendar={showCalendar}
      />
      <div ref={dragRef} className="sidebar-resize-handle" onMouseDown={onResizeMouseDown} />
      </div>

      {!showCalendar && (
        <EmailList
          messages={messages}
          selectedId={selectedMessage?.id}
          onSelect={handleSelectMessage}
          onSearch={handleSearch}
          onFilterChange={handleFilterChange}
          filter={filter}
          loading={loadingMessages}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
          categoryMap={categoryMap}
          onMessageUpdate={handleMessageUpdate}
          pinnedIds={pinnedIds}
          onTogglePin={handleTogglePin}
          folderName={selectedFolder?.displayName}
          onRefresh={() => { loadMessages(selectedFolder, search, 0, filter); getFolders().then(data => { setFolders(data); setFolderRefreshTick(t => t + 1); }).catch(() => {}); }}
        />
      )}

      <main className={`main-panel ${showCalendar ? 'calendar-expanded' : ''}`}>
        {error && <div className="error-banner">{error} <button onClick={() => setError('')}>✕</button></div>}
        {undoState && (
          <div className="undo-toast">
            <span>{undoState.label}</span>
            <button className="undo-btn" onClick={handleUndo}>Undo</button>
            <button className="undo-dismiss" onClick={() => { clearTimeout(undoTimerRef.current); setUndoState(null); }}>✕</button>
          </div>
        )}
        {showCalendar ? (
          <CalendarView />
        ) : showUsers ? (
          <UserManagement onClose={() => setShowUsers(false)} />
        ) : showSettings ? (
          <Settings signature={signature} onSave={handleSaveSignature} onCancel={() => setShowSettings(false)} />
        ) : composing ? (
          <Compose signature={signature} onCancel={() => setComposing(false)} />
        ) : loadingMessage ? (
          <div className="email-view-empty"><div className="loading-spinner" /></div>
        ) : (
          <EmailView
            message={selectedMessage}
            folders={folders}
            selectedFolderId={selectedFolder?.id}
            categoryMap={categoryMap}
            allCategories={allCategories}
            pinnedIds={pinnedIds}
            onTogglePin={handleTogglePin}
            signature={signature}
            onDeleted={handleDeleted}
            onMoved={handleMoved}
            onMessageUpdate={handleMessageUpdate}
            onRegisterUndo={registerUndo}
          />
        )}
      </main>
    </div>
  );
}
